import React, { Fragment, useState } from 'react';
import { Controller } from 'react-hook-form';

import Alert from '@/core/components/Alert';
import Button from '@/core/components/Button';
import FormContainer from '@/core/components/FormContainer';
import Header from '@/core/components/Header';
import RadioInput from '@/core/components/inputs/RadioInput';
import Separator from '@/core/components/Separator';
import TakerCard from '@/core/components/TakerCard';
import { ToastStatus } from '@/core/components/Toast/Toast';
import useForm from '@/core/hooks/useForm';
import { AnalyticsEventType, useAnalyticsContext } from '@/core/lib/analytics/analytics.context';
import Actions from '@/core/lib/new-architecture/actions';
import { useToastContext } from '@/core/lib/toast/toast.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { DonationDeleteFormType } from '@/core/types/donation';
import { donationDeleteFormSchema } from '@/core/types/donation';
import type { UserResult } from '@/core/types/users';

type DeleteDonationSteps = 'reasons' | 'takers';
export interface DonationDeleteFormProps {
  takers?: UserResult[];
  donationId: number;
  options: { label: string; id: string }[];
  onClose: () => void;
}

const DonationDeleteForm: React.FC<DonationDeleteFormProps> = ({ donationId, options, takers, onClose }) => {
  const { t } = useTranslationContext(['common']);
  const toast = useToastContext();
  const { send } = useAnalyticsContext();

  const firstStep: DeleteDonationSteps = 'reasons';
  const [step, setStep] = useState<DeleteDonationSteps>(firstStep);

  const { onDelete } = Actions.donation.useTerminateDonation(donationId);

  const { control, onSubmit, errors, onCleanGlobalError, watch, reset, isValid } = useForm<unknown, DonationDeleteFormType>({
    schema: donationDeleteFormSchema,
    mutationFn: data => {
      return onDelete(data);
    },
    onSuccess: () => {
      send({ event: AnalyticsEventType.COMPLETED_POST });
      reset();
      toast?.open(t('components.donation.delete.toast.success', { ns: 'common' }), 3000, ToastStatus.SUCCESS);
      onClose();
    },
    onError: () => {
      toast?.open(t('components.donation.delete.toast.error', { ns: 'common' }), 3000, ToastStatus.DANGER);
    },
  });

  const reason = watch('reason');
  const taker = watch('taker');

  const isReasonChecked = (value: string) => {
    return value === reason;
  };
  const isTakerChecked = (value: string) => {
    return value === taker;
  };

  const handleSubmit = async () => {
    if (reason === 'give_donnons' && takers && takers.length > 0) {
      setStep('takers');
      return;
    }
    onSubmit();
  };

  const onPreviousStep = () => {
    setStep('reasons');
  };

  const ReasonsStep: React.ReactNode = (
    <div className="inline-flex h-full flex-col md:gap-9">
      <div className="inline-flex flex-col gap-4">
        <p className="text-body-primary font-semibold">{t('components.donation.delete.reasons.title', { ns: 'common' })}</p>

        <FormContainer>
          <Controller
            control={control}
            render={({ field: { onChange, onBlur } }) => {
              return (
                <div className="flex flex-col gap-4">
                  {options.map((opt, i) => {
                    return (
                      <Fragment key={opt.id}>
                        <RadioInput id={opt.id} value={opt.id} checked={isReasonChecked(opt.id)} onChange={onChange} onBlur={onBlur}>
                          <span className="text-body-secondary">{opt.label}</span>
                        </RadioInput>
                        {i !== options.length - 1 && <Separator />}
                      </Fragment>
                    );
                  })}
                </div>
              );
            }}
            name="reason"
          />
        </FormContainer>
      </div>
      <div className="mt-auto flex w-full justify-end">
        <div className="w-full md:max-w-max">
          <Button type="submit" variant="primary" size="large" isDisabled={!isValid} onClick={handleSubmit}>
            {t('components.donation.delete.reasons.submit.text', { ns: 'common' })}
          </Button>
        </div>
      </div>
    </div>
  );

  const TakersStep: React.ReactNode = (
    <div className="inline-flex h-full flex-col md:gap-9">
      <p className="text-body-primary font-semibold">{t('components.donation.delete.takers.title', { ns: 'common' })}</p>
      <FormContainer>
        <Controller
          control={control}
          render={({ field: { onChange, onBlur } }) => {
            return (
              <div className="flex flex-col">
                {takers?.map(user => (
                  <TakerCard key={user.uuid} taker={user} checked={isTakerChecked(user.uuid)} onChange={onChange} onBlur={onBlur} />
                ))}
              </div>
            );
          }}
          name="taker"
        />
      </FormContainer>
      <div className="mt-auto flex w-full justify-end">
        <div className="w-full md:max-w-max">
          <Button type="submit" variant="primary" size="large" onClick={onSubmit} isDisabled={!taker}>
            {t('components.donation.delete.takers.submit.text', { ns: 'common' })}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col gap-5">
      {step === 'reasons' && <Header content={t('components.donation.delete.reasons.nav-content', { ns: 'common' })} iconAfter="close" onAfter={onClose} />}
      {step === 'takers' && (
        <Header
          content={t('components.donation.delete.takers.nav-content', { ns: 'common' })}
          iconBefore="arrow-left"
          labelBefore={t('components.donation.delete.takers.nav-back', { ns: 'common' })}
          onBefore={onPreviousStep}
        />
      )}

      {errors.global.isError && errors.global.message && (
        <Alert status="error" onClose={onCleanGlobalError}>
          {t(errors.global.message, { ns: 'common' })}
        </Alert>
      )}

      {step === 'reasons' && ReasonsStep}
      {step === 'takers' && TakersStep}
    </div>
  );
};

export default DonationDeleteForm;
